import React, {createContext, useEffect, useMemo, useState} from "react";
import {flow, isEmpty} from "lodash-es";
import {useSelector} from "react-redux";
import {selectInvestigationGraphItems} from "store/selectors/investigationSelectors";
import {
  DEFAULT_COMBINE_ON,
  LAYOUT_OPTIONS,
  LAYOUTS
} from "utils/graph/graphStyleConstants";
import {getEntityTypes, isEdge, isNode} from "utils/graph/graphUtils";

const RelationsGraphContext = createContext({
  graphModel: {},
  graphNodeCount: 0,
  timebarModel: {},
  layout: {...LAYOUTS[LAYOUT_OPTIONS[0].value]},
  entityTypes: [],
  selectedTypes: [],
  openCombos: {},
  combine: DEFAULT_COMBINE_ON,
  setGraphModel: (state) => {},
  setLayout: (state) => {},
  setSelectedTypes: (state) => {},
  handleTimebarChange: ({items}) => {},
  setOpenCombos: () => {},
  setCombine: () => {}
});

const RelationsGraphProvider = ({children}) => {
  /* dataModel: This is the entire node/edge data model as retrieved from Redux store. */
  const dataModel = useSelector(selectInvestigationGraphItems);

  /**
   * timebarModel: The view of dataModel for the Timebar. This is all dataModel with filtered items removed.
   */
  const [timebarModel, setTimebarModel] = useState({});
  /**
   * graphModel: This is the view of dataModel for the Graph. This only the list of dataModel determined to be in-view by
   * the Timebar sliding window.
   */
  const [graphModel, setGraphModel] = useState({});

  /** Other Shared graph state */
  const [layout, setLayout] = useState({...LAYOUTS[LAYOUT_OPTIONS[0].value]});
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [openCombos, setOpenCombos] = useState({});
  const [combine, setCombine] = useState(DEFAULT_COMBINE_ON);

  const entityTypes = useMemo(
    () => getEntityTypes(Object.values(dataModel).filter((i) => isNode(i))),
    [dataModel]
  );

  useEffect(() => {
    if (!isEmpty(dataModel)) {
      const timeRangeData = filterByEntityType(dataModel);
      setTimebarModel(timeRangeData);
    }
  }, [dataModel, selectedTypes]);

  const graphNodeCount = Object.entries(graphModel).filter(([, v]) =>
    isNode(v)
  ).length;

  /**
   * Handler for Timebar 'onChange'.
   * Will trigger when timebarModel changes or user manually changes sliding window.
   * Therefore graphModel should be set from here, not in separate useEffect.
   *
   * items: All items in range of Timebar sliding window. timebarModel has already had filters applied.
   */
  const handleTimebarChange = ({items}) => {
    setGraphModel(items);
  };

  const filterByEntityType = (data) => {
    if (!selectedTypes.length) {
      return data;
    }

    return flow([
      Object.entries,
      (arr) =>
        arr.filter(
          ([, value]) => isEdge(value) || isSelectedType(value?.data?.type)
        ),
      Object.fromEntries
    ])(data);
  };

  const isSelectedType = (type) => selectedTypes.includes(type);

  return (
    <RelationsGraphContext.Provider
      value={{
        graphModel,
        graphNodeCount,
        timebarModel,
        layout,
        entityTypes,
        selectedTypes,
        openCombos,
        combine,
        setGraphModel,
        setLayout,
        setSelectedTypes,
        handleTimebarChange,
        setOpenCombos,
        setCombine
      }}>
      {children}
    </RelationsGraphContext.Provider>
  );
};

export {RelationsGraphContext, RelationsGraphProvider};
